import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAdminEmailServers } from '@/features/admin-email/api';
import {
  useTemplateVariables,
  type TemplateVariableDto,
  type TemplateVariableGroup,
} from '@/features/template-variables/api';

type TextField = 'emailSubject' | 'emailBodyHtml' | 'emailBodyText';

export interface QuestionnaireEmailValues {
  readonly emailSubject: string;
  readonly emailBodyHtml: string;
  readonly emailBodyText: string;
  readonly emailServerId: string;
}

const GROUP_ORDER: readonly TemplateVariableGroup[] = ['PATIENT', 'QUESTIONNAIRE', 'CLINIC'];

/**
 * The email half of a questionnaire-template form (PTV2-1162): subject + HTML/text bodies, the
 * sending-server picker, and the variable palette. Clicking a palette chip inserts {@code {{key}}}
 * at the caret of whichever field was last focused.
 */
export function QuestionnaireEmailFields({
  idPrefix,
  values,
  onChange,
}: {
  readonly idPrefix: string;
  readonly values: QuestionnaireEmailValues;
  readonly onChange: (field: keyof QuestionnaireEmailValues, value: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const servers = useAdminEmailServers();
  const variables = useTemplateVariables('PPQ_EMAIL');

  const subjectRef = useRef<HTMLInputElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [activeField, setActiveField] = useState<TextField>('emailBodyHtml');

  function refFor(field: TextField): HTMLInputElement | HTMLTextAreaElement | null {
    if (field === 'emailSubject') return subjectRef.current;
    if (field === 'emailBodyHtml') return htmlRef.current;
    return textRef.current;
  }

  function insertVariable(key: string): void {
    const token = `{{${key}}}`;
    const field = activeField;
    const el = refFor(field);
    const current = values[field];
    if (el !== null) {
      const start = el.selectionStart ?? current.length;
      const end = el.selectionEnd ?? start;
      const nextValue = current.slice(0, start) + token + current.slice(end);
      onChange(field, nextValue);
      const caret = start + token.length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    } else {
      onChange(field, current + token);
    }
  }

  const fieldPrefix = 'routes.admin.questionnaires.templates.form.email';
  const grouped = GROUP_ORDER.map((group) => ({
    group,
    items: (variables.data ?? []).filter((v) => v.group === group),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-foreground">{t(`${fieldPrefix}.heading`)}</h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Columns 1–2: server + subject, then the two body editors. */}
        <div className="space-y-4 sm:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField id={`${idPrefix}-email-server`} label={t(`${fieldPrefix}.server`)}>
              {(fp) => (
                <Select
                  {...fp}
                  value={values.emailServerId}
                  onChange={(e) => {
                    onChange('emailServerId', e.target.value);
                  }}
                >
                  <option value="">{t(`${fieldPrefix}.serverDefault`)}</option>
                  {(servers.data ?? []).map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>

            <FormField id={`${idPrefix}-email-subject`} label={t(`${fieldPrefix}.subject`)}>
              {(fp) => (
                <Input
                  {...fp}
                  ref={subjectRef}
                  value={values.emailSubject}
                  onFocus={() => {
                    setActiveField('emailSubject');
                  }}
                  onChange={(e) => {
                    onChange('emailSubject', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField id={`${idPrefix}-email-body-html`} label={t(`${fieldPrefix}.bodyHtml`)}>
              {(fp) => (
                <Textarea
                  {...fp}
                  ref={htmlRef}
                  rows={8}
                  value={values.emailBodyHtml}
                  onFocus={() => {
                    setActiveField('emailBodyHtml');
                  }}
                  onChange={(e) => {
                    onChange('emailBodyHtml', e.target.value);
                  }}
                  className="font-mono text-xs"
                />
              )}
            </FormField>

            <FormField id={`${idPrefix}-email-body-text`} label={t(`${fieldPrefix}.bodyText`)}>
              {(fp) => (
                <Textarea
                  {...fp}
                  ref={textRef}
                  rows={8}
                  value={values.emailBodyText}
                  onFocus={() => {
                    setActiveField('emailBodyText');
                  }}
                  onChange={(e) => {
                    onChange('emailBodyText', e.target.value);
                  }}
                />
              )}
            </FormField>
          </div>
        </div>

        {/* Column 3: variable palette — description, then one row per group, one row per variable. */}
        {variables.isSuccess && grouped.length > 0 && (
          <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">{t(`${fieldPrefix}.palette.hint`)}</p>
            {grouped.map(({ group, items }) => (
              <div key={group} className="space-y-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t(`${fieldPrefix}.palette.groups.${group}`)}
                </p>
                <div className="flex flex-col items-stretch gap-1">
                  {items.map((variable: TemplateVariableDto) => (
                    <button
                      key={variable.key}
                      type="button"
                      title={variable.description}
                      onClick={() => {
                        insertVariable(variable.key);
                      }}
                      className="rounded border border-border bg-card px-2 py-1 text-left font-mono text-xs text-foreground/90 hover:bg-accent"
                    >
                      {`{{${variable.key}}}`}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
